import { API_URL, API_TIMEOUT } from '@/@vxapp/constants'

/** HTTP methods allowed for API endpoints */
export type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch'

/** Common configuration for any API endpoint */
export interface EndpointConfig {
  url: string
  method?: HttpMethod
  timeout?: number
  identifierKey?: string
  headers?: Record<string, string>
}

export interface BankingCrudEndpointGroup {
  Name: string
  FetchTemplate: EndpointConfig
  FetchAll: EndpointConfig
  Fetch: EndpointConfig
  Create: EndpointConfig
  Update: EndpointConfig
  Delete: EndpointConfig
}

export interface MemberEndpointConfig extends EndpointConfig {
  identifierKey: string
}

export interface TenantCrudEndpointGroup {
  FetchTemplate: EndpointConfig
  FetchAll: EndpointConfig
  Fetch: MemberEndpointConfig
  Create: EndpointConfig
  Update: MemberEndpointConfig
  Delete: MemberEndpointConfig
}

/** Schema for the application's endpoints */
export interface EndpointSchema {
  TenantUsers: TenantCrudEndpointGroup
  TenantRoles: TenantCrudEndpointGroup
  TenantRolePermissions: TenantCrudEndpointGroup
  TenantPermissionDefinitions: TenantCrudEndpointGroup & { FetchCatalog: EndpointConfig }

  Sbr: {
    Submissions: {
      FetchAll: EndpointConfig
      Fetch: EndpointConfig
    }
  }
  Vxapp: {
    Api: { url: string; timeout: number }
  }
  Auth: {
    guests: EndpointConfig
    login: EndpointConfig
    refresh: EndpointConfig
    permissions: EndpointConfig
    Roles: {
      Fetch: EndpointConfig
    }
    PasswordReset: {
      Request: EndpointConfig // POST /Kernal/Users/Auth/PasswordReset/Request (public, no Authorization)
      Validate: EndpointConfig // POST /Kernal/Users/Auth/PasswordReset/Validate (public, explicit Vx-Api-Guid)
      Confirm: EndpointConfig // POST /Kernal/Users/Auth/PasswordReset/Confirm (public, explicit Vx-Api-Guid)
    }
  }
  Kernal: {
    Keys: {
      Google: EndpointConfig
    }
  }
  Sites: {
    Name: string
    FetchTemplate: EndpointConfig
    FetchAll: EndpointConfig
    Fetch: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    Themes: {
      FetchAll: EndpointConfig
    }
  }
  Agents: {
    Tools: {
      PreviewCreateSitePage: EndpointConfig
      PreviewUpdateSitePage: EndpointConfig
      ConfirmSitePageMutation: EndpointConfig
    }
  }

  Contacts: {
    FetchAll: EndpointConfig
  }
  Cron: {
    ProcessMessagesQueue: EndpointConfig
    SendScheduledMessages: EndpointConfig
    ProcessUpcomingOutstandingOrders: EndpointConfig
    ReloadAllInstagramFeeds: EndpointConfig
    ProcessInvoicesOverdue: EndpointConfig
    GenerateRecurringQuotes: EndpointConfig
    SyncProductInstagramImages: EndpointConfig
    ScheduleWelcomeEmails: EndpointConfig
  }
  Calendar: {
    Delete: EndpointConfig
  }
  Banking: {
    Accounts: BankingCrudEndpointGroup
    Journals: BankingCrudEndpointGroup
    Rules: BankingCrudEndpointGroup
    TaxCodes: BankingCrudEndpointGroup
    Transactions: BankingCrudEndpointGroup & {
      Files: EndpointConfig
    }
  }
  Assets: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    FetchTemplate: EndpointConfig
  }
  BookingOptionSelections: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    FetchTemplate: EndpointConfig
  }
  BookingOptions: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    FetchTemplate: EndpointConfig
  }
  Contracts: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    FetchTemplate: EndpointConfig
  }
  Products: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    FetchTemplate: EndpointConfig
    Files: EndpointConfig
    Events: EndpointConfig
    MessagesVariables: EndpointConfig
    MessagesVariablesByGUID: EndpointConfig
  }
  ProductCategories: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    FetchTemplate: EndpointConfig
  }
  Rules: {}
  Customers: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    FetchTemplate: EndpointConfig
    Files: EndpointConfig
    MessagesVariables: EndpointConfig
  }
  Messages: {
    Create: EndpointConfig
    Update: EndpointConfig
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    FetchTemplate: EndpointConfig
    Delete: EndpointConfig
    ProcessVariables: EndpointConfig
    FetchWeeklyMetrics: EndpointConfig
    FetchMonthlyMetrics: EndpointConfig
    RenderTemplatedMessage: EndpointConfig
  }
  Templates: {
    Create: EndpointConfig
    Update: EndpointConfig
  }
  Themes: {
    Create: EndpointConfig
    Update: EndpointConfig
  }
  BankAccounts: {
    Name: string
    Fetch: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    LoadNew: EndpointConfig
  }
  Businesses: {
    Fetch: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Contact: EndpointConfig
  }
  Files: {
    FileManager: EndpointConfig
    ProcessingJobs: EndpointConfig
  }
  JobTypes: {
    Fetch: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
  }
  Jobs: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
    FetchTemplate: EndpointConfig
  }

  Orders: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    CreateTemplatedMessage: EndpointConfig
    FetchTemplatedMessage: EndpointConfig
  }
  Invoices: {
    Name: string
    Fetch: EndpointConfig
    FetchAll: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    CreateTemplatedMessage: EndpointConfig
    FetchIterableTemplatedMessage: EndpointConfig
    FetchTemplatedMessage: EndpointConfig
    StatusMetrics: EndpointConfig
    Stripe: {
      ClientSecret: EndpointConfig
    }
    Events: {
      CreateOrderInvoice: EndpointConfig
    }
  }
  Quotes: {
    Name: string
    FetchTemplate: EndpointConfig
    Update: EndpointConfig
    CreateTemplatedMessage: EndpointConfig
    FetchIterableTemplatedMessage: EndpointConfig
    FetchTemplatedMessage: EndpointConfig
    StatusMetrics: EndpointConfig
    Fetch: EndpointConfig
  }
  Calculators: { calculateTotals: EndpointConfig }
  MetaOAuth: {
    Initiate: EndpointConfig
    Callback: EndpointConfig
    Status: EndpointConfig
    Disconnect: EndpointConfig
  }
  GoogleOAuth: {
    Initiate: EndpointConfig
    Status: EndpointConfig
    Disconnect: EndpointConfig
  }
  MetaCompliance: {
    Deauthorization: EndpointConfig
    DataDeletion: EndpointConfig
    DataDeletionStatus: EndpointConfig
  }
  InstagramAccounts: {
    FetchAll: EndpointConfig
    Fetch: EndpointConfig
    Media: EndpointConfig
  }
  Stripe: {
    Config: {
      PublicKey: EndpointConfig
    }
    Sessions: {
      Fetch: EndpointConfig
    }
    Accounts: {
      FetchTemplate: EndpointConfig
      FetchAll: EndpointConfig
      Fetch: EndpointConfig
      Create: EndpointConfig
      Update: EndpointConfig
      Delete: EndpointConfig
    }
  }
  PayPalAccounts: {
    FetchTemplate: EndpointConfig
    FetchAll: EndpointConfig
    Fetch: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
  }
  GooglePlacesSettings: {
    FetchTemplate: EndpointConfig
    FetchByOwner: EndpointConfig
    FetchMergedSite: EndpointConfig
    /** Deprecated alias retained for callers that have not moved to Site. */
    FetchMergedBusinessAlias: EndpointConfig
    FetchMergedProduct: EndpointConfig
    Create: EndpointConfig
    Update: EndpointConfig
    Delete: EndpointConfig
  }
  MicrosoftOAuth: {
    // OAuth flow (user-facing)
    GetAuthUrl: EndpointConfig // GET  /OAuth/Microsoft → { authorizationUrl, state }
    Status: EndpointConfig // GET  /OAuth/Microsoft/Status → { isLinked, email, displayName, isTokenExpired, lastRefreshed, linkedAt }
    Revoke: EndpointConfig // DELETE /OAuth/Microsoft
    Refresh: EndpointConfig // POST /OAuth/Microsoft/Refresh → force token refresh
    // Admin token CRUD (UCV5 standard CRUD)
    Token: {
      FetchAll: EndpointConfig // GET  /MicrosoftOAuth
      Fetch: EndpointConfig // GET  /MicrosoftOAuth/{serviceIdentifier}
      Create: EndpointConfig // POST /MicrosoftOAuth
      Update: EndpointConfig // PATCH /MicrosoftOAuth/{serviceIdentifier}
      Delete: EndpointConfig // DELETE /MicrosoftOAuth/{serviceIdentifier}
    }
    // Mail operations via Microsoft Graph
    Mail: {
      FetchAll: EndpointConfig // GET  /Mail/Microsoft (paginated list)
      Fetch: EndpointConfig // GET  /Mail/Microsoft/{messageId}
      Send: EndpointConfig // POST /Mail/Microsoft/Send
      Update: EndpointConfig // PATCH /Mail/Microsoft/{messageId}
      Move: EndpointConfig // POST /Mail/Microsoft/{messageId}/Move
      FetchFolders: EndpointConfig // GET  /Mail/Microsoft/Folders
      Mailboxes: EndpointConfig // GET/POST /Mail/Microsoft/Mailboxes
      Compose: EndpointConfig // POST /Mail/Microsoft/Compose
      Reply: EndpointConfig // POST /Mail/Microsoft/Reply/{messageId}
      ReplyAll: EndpointConfig // POST /Mail/Microsoft/ReplyAll/{messageId}
      Forward: EndpointConfig // POST /Mail/Microsoft/Forward/{messageId}
      FetchDraft: EndpointConfig // GET /Mail/Microsoft/Drafts/{draftGUID}
      UpdateDraft: EndpointConfig // PATCH /Mail/Microsoft/Drafts/{draftGUID}
      DiscardDraft: EndpointConfig // DELETE /Mail/Microsoft/Drafts/{draftGUID}
      SendDraft: EndpointConfig // POST /Mail/Microsoft/Drafts/{draftGUID}/Send
      DraftAttachments: EndpointConfig // GET/POST/DELETE /Mail/Microsoft/Drafts/{draftGUID}/Attachments
      FetchMessageAttachments: EndpointConfig // GET /Mail/Microsoft/{messageId}/Attachments
      FetchMessageAttachment: EndpointConfig // GET /Mail/Microsoft/{messageId}/Attachments/{attachmentId}
    }
  }
}

/**
 * Central definition of all API endpoints used throughout the app.
 * No trailing slash on GET requests with GUID parameters.
 */
export const Endpoint: EndpointSchema = {
  TenantUsers: {
    FetchTemplate: { url: '/TenantUsers/load_new', method: 'get' },
    FetchAll: { url: '/TenantUsers', method: 'get' },
    Fetch: { url: '/TenantUsers/{userGUID}', method: 'get', identifierKey: 'userGUID' },
    Create: { url: '/TenantUsers', method: 'post' },
    Update: { url: '/TenantUsers/{userGUID}', method: 'patch', identifierKey: 'userGUID' },
    Delete: { url: '/TenantUsers/{userGUID}', method: 'delete', identifierKey: 'userGUID' },
  },
  TenantRoles: {
    FetchTemplate: { url: '/TenantRoles/load_new', method: 'get' },
    FetchAll: { url: '/TenantRoles', method: 'get' },
    Fetch: { url: '/TenantRoles/{roleGUID}', method: 'get', identifierKey: 'roleGUID' },
    Create: { url: '/TenantRoles', method: 'post' },
    Update: { url: '/TenantRoles/{roleGUID}', method: 'patch', identifierKey: 'roleGUID' },
    Delete: { url: '/TenantRoles/{roleGUID}', method: 'delete', identifierKey: 'roleGUID' },
  },
  TenantRolePermissions: {
    FetchTemplate: { url: '/TenantRolePermissions/load_new', method: 'get' },
    FetchAll: { url: '/TenantRolePermissions', method: 'get' },
    Fetch: {
      url: '/TenantRolePermissions/{permissionGUID}',
      method: 'get',
      identifierKey: 'permissionGUID',
    },
    Create: { url: '/TenantRolePermissions', method: 'post' },
    Update: {
      url: '/TenantRolePermissions/{permissionGUID}',
      method: 'patch',
      identifierKey: 'permissionGUID',
    },
    Delete: {
      url: '/TenantRolePermissions/{permissionGUID}',
      method: 'delete',
      identifierKey: 'permissionGUID',
    },
  },
  TenantPermissionDefinitions: {
    FetchTemplate: { url: '/TenantPermissionDefinitions/load_new', method: 'get' },
    FetchAll: { url: '/TenantPermissionDefinitions', method: 'get' },
    Fetch: {
      url: '/TenantPermissionDefinitions/{permissionDefinitionGUID}',
      method: 'get',
      identifierKey: 'permissionDefinitionGUID',
    },
    Create: { url: '/TenantPermissionDefinitions', method: 'post' },
    Update: {
      url: '/TenantPermissionDefinitions/{permissionDefinitionGUID}',
      method: 'patch',
      identifierKey: 'permissionDefinitionGUID',
    },
    Delete: {
      url: '/TenantPermissionDefinitions/{permissionDefinitionGUID}',
      method: 'delete',
      identifierKey: 'permissionDefinitionGUID',
    },
    FetchCatalog: { url: '/TenantPermissionDefinitions/catalog', method: 'get' },
  },
  Sbr: {
    Submissions: {
      FetchAll: { url: '/Sbr/Submissions', method: 'get' },
      Fetch: { url: '/Sbr/Submissions', method: 'get' },
    },
  },
  Vxapp: {
    Api: { url: API_URL, timeout: API_TIMEOUT },
  },

  Auth: {
    guests: { url: '/Kernal/Guests/Auth', method: 'post' },
    login: { url: '/Kernal/Users/Auth/Login', method: 'post' },
    refresh: { url: '/Kernal/Auth/Renew', method: 'post' },
    permissions: { url: '/Kernal/Auth/Roles/Permissions', method: 'get' },

    Roles: {
      Fetch: { url: '/Kernal/Auth/Roles', method: 'get' },
    },

    PasswordReset: {
      Request: { url: '/Kernal/Users/Auth/PasswordReset/Request', method: 'post' },
      Validate: { url: '/Kernal/Users/Auth/PasswordReset/Validate', method: 'post' },
      Confirm: { url: '/Kernal/Users/Auth/PasswordReset/Confirm', method: 'post' },
    },
  },

  Kernal: {
    Keys: {
      Google: { url: '/Kernal/Keys/Google', method: 'post' },
    },
  },

  Sites: {
    Name: 'Sites',
    FetchTemplate: { url: '/Sites/load_new', method: 'post' },
    FetchAll: { url: '/Sites', method: 'get' },
    Fetch: { url: '/Sites', method: 'get' },
    Create: { url: '/Sites', method: 'post' },
    Update: { url: '/Sites', method: 'patch' },
    Delete: { url: '/Sites', method: 'delete' },
    Themes: {
      FetchAll: { url: '/Sites/Themes', method: 'get' },
    },
  },

  Agents: {
    Tools: {
      PreviewCreateSitePage: { url: '/Agents/Tools/preview_create_site_page', method: 'post' },
      PreviewUpdateSitePage: { url: '/Agents/Tools/preview_update_site_page', method: 'post' },
      ConfirmSitePageMutation: { url: '/Agents/Tools/confirm_site_page_mutation', method: 'post' },
    },
  },

  Contacts: {
    FetchAll: { url: '/Contacts', method: 'get' },
  },

  Cron: {
    ProcessMessagesQueue: { url: '/Cron/Process/Messages/Queue', method: 'get', timeout: 20000 },
    SendScheduledMessages: {
      url: '/Cron/Process/Messages/Scheduled',
      method: 'get',
      timeout: 20000,
    },
    ProcessUpcomingOutstandingOrders: {
      url: '/Cron/Orders/Billing/Messages/Outstanding',
      method: 'get',
      timeout: 20000,
    },
    ReloadAllInstagramFeeds: {
      url: '/Cron/SocialMedia/Instagram/ReloadAllFeeds',
      method: 'get',
      timeout: 20000,
    },
    ProcessInvoicesOverdue: {
      url: '/Cron/Process/Invoices/Overdue',
      method: 'get',
      timeout: 20000,
    },
    GenerateRecurringQuotes: {
      url: '/Cron/Process/Recurring/Quotes',
      method: 'get',
      timeout: 20000,
    },
    SyncProductInstagramImages: {
      url: '/Cron/Process/Products/Instagram/Images',
      method: 'get',
      timeout: 20000,
    },
    ScheduleWelcomeEmails: {
      url: '/Cron/Orders/Messages/WelcomeMessages',
      method: 'get',
      timeout: 20000,
    },
  },

  Calendar: {
    Delete: { url: '/Calendar', method: 'delete' },
  },
  Banking: {
    Accounts: {
      Name: 'Accounts',
      FetchTemplate: { url: '/Banking/Accounts/load_new', method: 'get' },
      FetchAll: { url: '/Banking/Accounts', method: 'get' },
      Fetch: { url: '/Banking/Accounts', method: 'get' },
      Create: { url: '/Banking/Accounts', method: 'post' },
      Update: { url: '/Banking/Accounts', method: 'patch' },
      Delete: { url: '/Banking/Accounts', method: 'delete' },
    },
    Journals: {
      Name: 'Journals',
      FetchTemplate: { url: '/Banking/Journals/load_new', method: 'get' },
      FetchAll: { url: '/Banking/Journals', method: 'get' },
      Fetch: { url: '/Banking/Journals', method: 'get' },
      Create: { url: '/Banking/Journals', method: 'post' },
      Update: { url: '/Banking/Journals', method: 'patch' },
      Delete: { url: '/Banking/Journals', method: 'delete' },
    },
    Rules: {
      Name: 'Rules',
      FetchTemplate: { url: '/Banking/Rules/load_new', method: 'get' },
      FetchAll: { url: '/Banking/Rules', method: 'get' },
      Fetch: { url: '/Banking/Rules', method: 'get' },
      Create: { url: '/Banking/Rules', method: 'post' },
      Update: { url: '/Banking/Rules', method: 'patch' },
      Delete: { url: '/Banking/Rules', method: 'delete' },
    },
    TaxCodes: {
      Name: 'TaxCodes',
      FetchTemplate: { url: '/Banking/TaxCodes/load_new', method: 'get' },
      FetchAll: { url: '/Banking/TaxCodes', method: 'get' },
      Fetch: { url: '/Banking/TaxCodes', method: 'get' },
      Create: { url: '/Banking/TaxCodes', method: 'post' },
      Update: { url: '/Banking/TaxCodes', method: 'patch' },
      Delete: { url: '/Banking/TaxCodes', method: 'delete' },
    },
    Transactions: {
      Name: 'Transactions',
      FetchTemplate: { url: '/Banking/Transactions/load_new', method: 'get' },
      FetchAll: { url: '/Banking/Transactions', method: 'get' },
      Fetch: { url: '/Banking/Transactions', method: 'get' },
      Create: { url: '/Banking/Transactions', method: 'post' },
      Update: { url: '/Banking/Transactions', method: 'patch' },
      Delete: { url: '/Banking/Transactions', method: 'delete' },
      Files: { url: '/Banking/Transactions/Files', method: 'post' },
    },
  },
  Assets: {
    Name: 'Assets',
    Fetch: { url: '/Assets', method: 'get' },
    FetchAll: { url: '/Assets', method: 'get' },
    Create: { url: '/Assets', method: 'post' },
    Update: { url: '/Assets', method: 'patch' },
    Delete: { url: '/Assets', method: 'delete' },
    FetchTemplate: { url: '/Assets/load_new', method: 'get' },
  },
  BookingOptionSelections: {
    Name: 'BookingOptionSelections',
    Fetch: { url: '/Products/BookingOptions/Selections', method: 'get' },
    FetchAll: { url: '/Products/BookingOptions/Selections', method: 'get' },
    Create: { url: '/Products/BookingOptions/Selections', method: 'post' },
    Update: { url: '/Products/BookingOptions/Selections', method: 'patch' },
    Delete: { url: '/Products/BookingOptions/Selections', method: 'delete' },
    FetchTemplate: { url: '/Products/BookingOptions/Selections/load_new', method: 'get' },
  },
  BookingOptions: {
    Name: 'BookingOptions',
    Fetch: { url: '/Products/BookingOptions', method: 'get' },
    FetchAll: { url: '/Products/BookingOptions', method: 'get' },
    Create: { url: '/Products/BookingOptions', method: 'post' },
    Update: { url: '/Products/BookingOptions', method: 'patch' },
    Delete: { url: '/Products/BookingOptions', method: 'delete' },
    FetchTemplate: { url: '/Products/BookingOptions/load_new', method: 'get' },
  },
  Contracts: {
    Name: 'Contracts',
    Fetch: { url: '/Contracts', method: 'get' },
    FetchAll: { url: '/Contracts', method: 'get' },
    Create: { url: '/Contracts', method: 'post' },
    Update: { url: '/Contracts', method: 'patch' },
    Delete: { url: '/Contracts', method: 'delete' },
    FetchTemplate: { url: '/Contracts/load_new', method: 'get' },
  },
  Products: {
    Name: 'Products',
    Fetch: { url: '/Products', method: 'get' },
    FetchAll: { url: '/Products', method: 'get' },
    Create: { url: '/Products', method: 'post' },
    Update: { url: '/Products', method: 'patch' },
    FetchTemplate: { url: '/Products/load_new', method: 'get' },
    Files: { url: '/Products/Files', method: 'post' },
    Events: { url: '/Products/Events', method: 'get' },
    MessagesVariables: { url: '/Products/Messages/Variables', method: 'get' },
    MessagesVariablesByGUID: { url: '/Products/Messages/Variables', method: 'get' },
  },

  ProductCategories: {
    Name: 'ProductCategories',
    Fetch: { url: '/Products/Categories', method: 'get' },
    FetchAll: { url: '/Products/Categories', method: 'get' },
    Create: { url: '/Products/Categories', method: 'post' },
    Update: { url: '/Products/Categories', method: 'patch' },
    Delete: { url: '/Products/Categories', method: 'delete' },
    FetchTemplate: { url: '/Products/Categories/load_new', method: 'get' },
  },

  Rules: {},
  Customers: {
    Name: 'Customers',
    Fetch: { url: '/Customers', method: 'get' },
    FetchAll: { url: '/Customers', method: 'get' },
    Create: { url: '/Customers', method: 'post' },
    Update: { url: '/Customers', method: 'patch' },
    Delete: { url: '/Customers', method: 'delete' },
    FetchTemplate: { url: '/Customers/load_new', method: 'get' },
    Files: { url: '/Customers/Files', method: 'post' },
    MessagesVariables: { url: '/Customers/Messages/Variables', method: 'get' },
  },
  Messages: {
    Create: { url: '/Messages', method: 'post' },
    Update: { url: '/Messages', method: 'patch' },
    Fetch: { url: '/Messages', method: 'get' },
    FetchAll: { url: '/Messages', method: 'get' },
    FetchTemplate: { url: '/Messages/load_new', method: 'get' },
    Delete: { url: '/Messages', method: 'delete' },
    ProcessVariables: { url: '/Messages/Variables/Processor', method: 'patch' },
    FetchWeeklyMetrics: { url: '/Messages/Metrics/Weekly', method: 'get', timeout: 20000 },
    FetchMonthlyMetrics: { url: '/Messages/Metrics/Monthly', method: 'get', timeout: 20000 },
    RenderTemplatedMessage: { url: '/Messages/Templates', method: 'get' },
  },
  Templates: {
    Create: { url: '/Messages/Templates', method: 'post' },
    Update: { url: '/Messages/Templates', method: 'patch' },
  },
  Themes: {
    Create: { url: '/Messages/Themes', method: 'post' },
    Update: { url: '/Messages/Themes', method: 'patch' },
  },
  BankAccounts: {
    Name: 'BankAccounts',
    Fetch: { url: '/BankAccounts', method: 'get' },
    Create: { url: '/BankAccounts', method: 'post' },
    Update: { url: '/BankAccounts', method: 'patch' },
    Delete: { url: '/BankAccounts', method: 'delete' },
    LoadNew: { url: '/BankAccounts/load_new', method: 'get' },
  },
  Businesses: {
    Fetch: { url: '/Businesses', method: 'get' },
    Create: { url: '/Businesses', method: 'post' },
    Update: { url: '/Businesses', method: 'patch' },
    Contact: { url: '/Businesses/Contact', method: 'post' },
  },
  JobTypes: {
    Fetch: { url: '/JobTypes', method: 'get' },
    Create: { url: '/JobTypes', method: 'post' },
    Update: { url: '/JobTypes', method: 'patch' },
  },
  Jobs: {
    Name: 'Jobs',
    Fetch: { url: '/Jobs', method: 'get' },
    FetchAll: { url: '/Jobs', method: 'get' },
    Create: { url: '/Jobs', method: 'post' },
    Update: { url: '/Jobs', method: 'patch' },
    Delete: { url: '/Jobs', method: 'delete' },
    FetchTemplate: { url: '/Jobs/load_new', method: 'get' },
  },

  Files: {
    FileManager: { url: '', method: 'get' }, // URL is packed with custom query params
    ProcessingJobs: { url: '/Files/Processing/Jobs', method: 'get' },
  },

  Orders: {
    Name: 'Orders',
    Fetch: { url: '/Orders', method: 'get' },
    FetchAll: { url: '/Orders', method: 'get' },
    CreateTemplatedMessage: { url: '/Orders/Messages', method: 'post' },
    FetchTemplatedMessage: { url: '/Orders/Messages/load_new', method: 'get' },
  },

  Invoices: {
    Name: 'Invoices',
    Fetch: { url: '/Invoices', method: 'get' },
    FetchAll: { url: '/Invoices', method: 'get' },
    Create: { url: '/Invoices', method: 'post' },
    Update: { url: '/Invoices', method: 'patch' },
    CreateTemplatedMessage: { url: '/Invoices/Messages', method: 'post' },
    FetchIterableTemplatedMessage: { url: '/Invoices/Messages', method: 'patch' },
    FetchTemplatedMessage: { url: '/Invoices/Messages/load_new', method: 'get' },
    StatusMetrics: { url: '/Invoices/Metrics/StatusMetrics', method: 'get' },
    Stripe: {
      ClientSecret: { url: '/Invoices/Stripe/ClientSecret', method: 'get' },
    },
    Events: {
      CreateOrderInvoice: { url: '/Invoices/Events/CreateOrderInvoice', method: 'patch' },
    },
  },

  Quotes: {
    Name: 'Quotes',
    FetchTemplate: { url: '/Quotes/load_new', method: 'get', timeout: 30000 },
    Update: { url: '/Quotes', method: 'patch' },
    CreateTemplatedMessage: { url: '/Quotes/Messages', method: 'post' },
    FetchIterableTemplatedMessage: { url: '/Quotes/Messages', method: 'patch' },
    FetchTemplatedMessage: { url: '/Quotes/Messages/load_new', method: 'get' },
    StatusMetrics: { url: '/Quotes/Metrics/StatusMetrics', method: 'get' },
    Fetch: { url: '/Quotes', method: 'get' },
  },

  Calculators: {
    calculateTotals: { url: '/Calculators/CalculateTotals', method: 'post' },
  },
  MetaOAuth: {
    Initiate: { url: '/OAuth/Meta/Instagram', method: 'post' },
    Callback: { url: '/OAuth/Meta/Instagram/Callback', method: 'get' },
    Status: { url: '/OAuth/Meta/Instagram/Status', method: 'get' },
    Disconnect: { url: '/OAuth/Meta/Instagram', method: 'delete' },
  },

  GoogleOAuth: {
    Initiate: { url: '/OAuth/Google', method: 'post' },
    Status: { url: '/OAuth/Google/Status', method: 'get' },
    Disconnect: { url: '/OAuth/Google', method: 'delete' },
  },

  MetaCompliance: {
    Deauthorization: { url: '/OAuth/Meta/Instagram/Deauthorization', method: 'post' },
    DataDeletion: { url: '/OAuth/Meta/Instagram/DataDeletion', method: 'post' },
    DataDeletionStatus: { url: '/OAuth/Meta/Instagram/DataDeletion', method: 'get' },
  },

  InstagramAccounts: {
    FetchAll: { url: '/InstagramAccounts', method: 'get' },
    Fetch: { url: '/InstagramAccounts', method: 'get' },
    Media: { url: '/InstagramAccounts', method: 'get' },
  },

  Stripe: {
    Config: {
      PublicKey: { url: '/Stripe/Config/PublicKey', method: 'get' },
    },
    Sessions: {
      Fetch: { url: '/Stripe/Sessions', method: 'get' },
    },
    Accounts: {
      FetchTemplate: { url: '/Stripe/Accounts/load_new', method: 'get' },
      FetchAll: { url: '/Stripe/Accounts', method: 'get' },
      Fetch: { url: '/Stripe/Accounts', method: 'get' },
      Create: { url: '/Stripe/Accounts', method: 'post' },
      Update: { url: '/Stripe/Accounts', method: 'patch' },
      Delete: { url: '/Stripe/Accounts', method: 'delete' },
    },
  },

  PayPalAccounts: {
    FetchTemplate: { url: '/PayPal/Accounts/load_new', method: 'get' },
    FetchAll: { url: '/PayPal/Accounts', method: 'get' },
    Fetch: { url: '/PayPal/Accounts', method: 'get' },
    Create: { url: '/PayPal/Accounts', method: 'post' },
    Update: { url: '/PayPal/Accounts', method: 'patch' },
    Delete: { url: '/PayPal/Accounts', method: 'delete' },
  },

  GooglePlacesSettings: {
    FetchTemplate: { url: '/GooglePlacesSettings/load_new', method: 'get' },
    FetchByOwner: { url: '/GooglePlacesSettings/Owner', method: 'get' },
    FetchMergedSite: { url: '/GooglePlacesSettings/Merged/Site', method: 'get' },
    FetchMergedBusinessAlias: { url: '/GooglePlacesSettings/Merged/Business', method: 'get' },
    FetchMergedProduct: { url: '/GooglePlacesSettings/Merged/Product', method: 'get' },
    Create: { url: '/GooglePlacesSettings', method: 'post' },
    Update: { url: '/GooglePlacesSettings', method: 'patch' },
    Delete: { url: '/GooglePlacesSettings', method: 'delete' },
  },

  MicrosoftOAuth: {
    GetAuthUrl: { url: '/OAuth/Microsoft', method: 'get' },
    Status: { url: '/OAuth/Microsoft/Status', method: 'get' },
    Revoke: { url: '/OAuth/Microsoft', method: 'delete' },
    Refresh: { url: '/OAuth/Microsoft/Refresh', method: 'post' },
    Token: {
      FetchAll: { url: '/MicrosoftOAuth', method: 'get' },
      Fetch: { url: '/MicrosoftOAuth', method: 'get' },
      Create: { url: '/MicrosoftOAuth', method: 'post' },
      Update: { url: '/MicrosoftOAuth', method: 'patch' },
      Delete: { url: '/MicrosoftOAuth', method: 'delete' },
    },
    Mail: {
      FetchAll: { url: '/Mail/Microsoft', method: 'get' },
      Fetch: { url: '/Mail/Microsoft', method: 'get' },
      Send: { url: '/Mail/Microsoft/Send', method: 'post' },
      Update: { url: '/Mail/Microsoft', method: 'patch' },
      Move: { url: '/Mail/Microsoft', method: 'post' },
      FetchFolders: { url: '/Mail/Microsoft/Folders', method: 'get' },
      Mailboxes: { url: '/Mail/Microsoft/Mailboxes', method: 'get' },
      Compose: { url: '/Mail/Microsoft/Compose', method: 'post' },
      Reply: { url: '/Mail/Microsoft', method: 'post' },
      ReplyAll: { url: '/Mail/Microsoft', method: 'post' },
      Forward: { url: '/Mail/Microsoft', method: 'post' },
      FetchDraft: { url: '/Mail/Microsoft/Drafts', method: 'get' },
      UpdateDraft: { url: '/Mail/Microsoft/Drafts', method: 'patch' },
      DiscardDraft: { url: '/Mail/Microsoft/Drafts', method: 'delete' },
      SendDraft: { url: '/Mail/Microsoft/Drafts', method: 'post' },
      DraftAttachments: { url: '/Mail/Microsoft/Drafts', method: 'get' },
      FetchMessageAttachments: { url: '/Mail/Microsoft', method: 'get' },
      FetchMessageAttachment: { url: '/Mail/Microsoft', method: 'get' },
    },
  },

  /*

  Blanks: {
    Fetch: { url: '/Blanks', method: 'get' },
    Create: { url: '/Blanks', method: 'post' },
    Update: { url: '/Blanks', method: 'patch' },
    FetchTemplate: { url: '/Blanks/load_new', method: 'get' },
    FetchAll: { url: '/Blanks', method: 'get' },
    Delete: { url: '/Blanks', method: 'delete' },
  },
  */
}
